import { faPen, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import store from 'redux/store';
import { deleteDashboard } from 'redux/slices/monitoringDashboardsSlice';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import { createInQuery } from 'actions/createInQuery';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';

type DashboardEntity = Extract<ActionEntity, { type: 'monitoring_dashboard' }>;

const isAllDashboard = (entities: readonly ActionEntity[]): entities is readonly DashboardEntity[] =>
  entities.every((entity) => entity.type === 'monitoring_dashboard');

/**
 * Action definitions for monitoring dashboards — a route-based type (view / edit
 * / create navigate to full pages). No transport for dashboards. `createDashboard`
 * is scoped; `view` / `edit` / `delete` are contextual. view/edit/create use the
 * navigate channel and report `'disabled'` when it's absent.
 */
export const dashboardActionDefinitions: ActionDefinitions = [
  {
    id: 'createDashboard',
    scope: 'type',
    label: 'New Dashboard',
    resolve: (ctx): ActionResult => {
      const { navigate, createIn } = ctx;
      return navigate ? () => navigate(`/monitoring/dashboards/new${createInQuery(createIn)}`) : 'disabled';
    },
  },
  {
    id: 'view',
    label: 'View Dashboard',
    icon: faEye,
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllDashboard(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => navigate(`/monitoring/dashboards/${only.id}/view`);
    },
  },
  {
    id: 'edit',
    label: 'Edit Dashboard',
    icon: faPen,
    resolve: (ctx): ActionResult => {
      const { entities, navigate } = ctx;
      if (!isAllDashboard(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      if (!navigate) return 'disabled';
      const [only] = entities;
      return () => navigate(`/monitoring/dashboards/${only.id}/edit`);
    },
  },
  {
    id: 'delete',
    label: 'Remove',
    icon: faTrash,
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllDashboard(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteDashboard(entity.id)), {
        refresh,
        confirmBody: 'Are you sure you want to delete this monitoring dashboard?',
      });
    },
  },
];
